import React from 'react';
import {Pane} from 'evergreen-ui';
import {useParams} from 'react-router-dom';
import {usePullDatasourceTSMutation} from 'Services/api/reportsApi';
import {useGetConfineUniqueQuery} from 'Services/api/stationConfinesApi';
import {backgroundColors} from 'Features/Reports/reportUtils';
import Chart from 'Shared/Chart/Chart';
import ChartWrapper from 'Shared/Chart/ChartWrapper';
import {TimeseriesContext} from './Timeseries.Context';
import {REPORT_DATA_TABLE_TYPE} from './Timeseries.Utils';
import {findUnit} from "../../Services/api/unitApi";
import {useTableDataLoader} from "./useTableDataLoader";

function shouldCorrect(realColumns) {
  let should = true
  Object.entries(realColumns).forEach(col => {
    if (col[1][0].stationDatasourceId !== null && col[1][0].stationDatasourceId !== undefined) {
      should = false
    }
  })

  return should
}

function correctColumns(realColumns, columns) {
  if (realColumns === null || realColumns === undefined || !shouldCorrect(realColumns)) {
    return columns
  }

  let newColumns = []
  columns.forEach(col => {
    let groupMeteocode = col.meteocode
    let groupColumns = realColumns[groupMeteocode]
    if (groupColumns === null || groupColumns === undefined) {
      newColumns.push(col)
      return
    }
    let stationEntityID = col.stationEntityId
    groupColumns.forEach(groupCol => {
      if (groupCol.stationEntityId === stationEntityID) {
        newColumns.push(groupCol)
      }
    })
  })

  return newColumns
}


export function Charts({ columnIndex = null, isDialog = false, paginate = null }) {
  const { columns, date, calcModuleParams, allMeteocodes, posts, confines, tableType } = React.useContext(TimeseriesContext);
  const {realColumns } = useTableDataLoader(date);
  const newColumns = React.useMemo(() => {
    if (realColumns === undefined || realColumns === null) {
      return null
    }
    return correctColumns(realColumns, columns)
  }, [realColumns, columns])
  const slicedColumns = React.useMemo(() => {
    if (newColumns === null) return []
    if (columnIndex === null) {
      return newColumns
    }
    return newColumns.slice(columnIndex || 0, columnIndex + 1);
  }, [columnIndex, columns, newColumns, realColumns]);

  const getOptions = React.useCallback(() => {
    const params = [];
    if (tableType === REPORT_DATA_TABLE_TYPE) {
      const uniqueMeteocodes = [];
      slicedColumns.forEach((column) => {
        if (!uniqueMeteocodes.find((umc) => umc.meteoCode === column.meteocode)) {
          const meteocode = allMeteocodes.find((mc) => mc.meteoCode === column.meteocode);
          if (meteocode) {
            uniqueMeteocodes.push(meteocode);
          }
        }
      });

      uniqueMeteocodes.forEach((meteocode) => {
        const columnsByMeteocode = slicedColumns.filter((column) => column.meteocode === meteocode.meteoCode);
        params.push({
          meteocode,
          calcModuleParams,
          columns: columnsByMeteocode.map((column) => ({
            meteocode: column.meteocode,
            sourceId: column.sourceId,
            sourceType: column.sourceType,
            stationEntityId: column.stationEntity?.id || -1,
            steps: column.steps,
            placeName: column.place,
          })),
          date,
          posts,
          confines,
        });
      });
    } else {
      slicedColumns.forEach((column) => {
        const meteocode = allMeteocodes.find((mc) => {
          if (mc.entityId) return mc.meteoCode === column.meteocode && mc.entityId === column.stationEntityId;
          return mc.meteoCode === column.meteocode;
        });
        params.push({
          meteocode,
          columns: [column],
          calcModuleParams,
          date: paginate || date,
          posts,
          confines,
        });
      });
    }
    return params;
  }, [allMeteocodes, calcModuleParams, confines, date, paginate, posts, slicedColumns, tableType]);

  const options = getOptions();

  React.useEffect(() => {
    getOptions();
  }, [getOptions]);

  return (
    <Pane height={isDialog ? 600 : '100%'} display="grid" overflow="auto">
      <TimeseriesCharts options={options} />
    </Pane>
  );
}

export function TimeseriesCharts({ options }) {
  return (
    <ChartWrapper>
      {(width, height) => {
        const potentialHeight = height / options.length;
        const minHeight = 350;
        const chartHeight = potentialHeight >= minHeight ? potentialHeight : minHeight;

        return React.Children.toArray(options.map((option) => (
          <SingleChart option={option} chartHeight={chartHeight} chartWidth={width} />
        )));
      }}
    </ChartWrapper>
  );
}

export function SingleChart({ option, chartHeight, chartWidth }) {
  const { stationId } = useParams();
  const [
    load, {
      data = {}, isLoading, isSuccess,
    },
  ] = usePullDatasourceTSMutation();

  const { data: confines = [], isFetching: isLoadingConfines } = useGetConfineUniqueQuery({
    stationId,
    code: option.meteocode.meteoCode,
  }, { skip: !stationId });

  const confinesMemo = React.useMemo(() => {
    const tmp = [];
    option.confines.forEach((confine) => {
      if (confines[confine.value] !== null) {
        tmp.push({
          label: confine.label,
          value: confines[confine.value],
        });
      }
    });
    return tmp;
  }, [confines, option.confines]);

  const {
    posts = [],
  } = option;

  const loadData = React.useCallback(async () => {
    const newBody = {
      ...option.calcModuleParams,
      doMeasureBreak: false,
      requestSeriesItems: option.columns.map((column) => ({
        ...option.date,
        meteoCode: column.meteocode,
        place: column.place || '',
        stationEntityId: column.stationEntityId,
        sourceId: column.sourceId,
        sourceType: column.sourceType,
        measures: column.steps,
        point: -1,
      })),
    };

    await load(newBody);
  }, [load,
    option.calcModuleParams.calc,
    option.calcModuleParams.includeOffGridData,
    option.calcModuleParams.round,
    option.columns,
    option.date.endDate,
    option.date.startDate,
  ]);

  const title = `[${option.meteocode.meteoCode}] ${option.meteocode.meteoCodeName} ${option.meteocode.entityName || option.meteocode.placeName || ''}`;
  const [unit, setUnit] = React.useState("")

  const getLabel = React.useCallback((index) => {
    const column = option.columns[index];
    let label = '';
    if (column) {
      const foundedPost = posts.find((post) => post.id === parseInt(column.sourceId, 10));
      if (foundedPost) {
        label = [foundedPost.idx || foundedPost.stationIdx, foundedPost.name].filter((p) => p).join(' - ');
      }
    }
    return label;
  }, [option.columns, posts]);

  const datasets = React.useMemo(() => {
    if (data.length > 0) {
      const original = data.map((values, index) => ({
        label: getLabel(index),
        data: values.response.map((r) => ({ timeUTC: r.t, value: r.v })),
        backgroundColor: backgroundColors[index], // цвет квадрата где пометка label
        borderColor: backgroundColors[index], // цвет линии,
      }));

      if (original.length === 1) {
        const trend = createTrend(original[0].data);

        if (trend) {
          original.push(trend);
        }
      }

      return original;
    }
    return [];
  }, [data, getLabel]);

  function returnUnit() {
    if (unit !== "") {
      return unit
    }
    let unitRequest = {mcode : option.meteocode.meteoCode};
    findUnit(unitRequest).then(
      (res) => {
        setUnit(res.data)
      }
    )
    return unit
  }
  return (
    <Chart
      datasets={datasets}
      chartOptions={{
        width: chartWidth,
        height: chartHeight,
        confines: confinesMemo,
        minDateMS: new Date(option.date.startDate).getTime(),
        maxDateMS: new Date(option.date.endDate).getTime(),
      }}
      chartTitle={title}
      loadOnClick
      loadOnClickOptions={{
        onClick: loadData,
        isLoading: isLoading || isLoadingConfines,
        isSuccess,
      }}
      unit={returnUnit()}
    />
  );
}

function createTrend(data) {
  const filteredData = data
    .filter((item) => item !== null)
    .map((item) => ({
      timeUTC: getTimeFromUTC(item.timeUTC),
      value: parseFloat(item.value),
    }))
    .filter((item) => !Number.isNaN(item.value));

  const { a, b } = getTrendCoefficients(filteredData);

  if (filteredData.length < 2) {
    return null;
  }

  const trendData = [
    {
      timeUTC: timeToUTC(filteredData[0].timeUTC),
      value: a * filteredData[0].timeUTC + b,
    },
    {
      timeUTC: timeToUTC(filteredData.at(-1).timeUTC),
      value: a * filteredData.at(-1).timeUTC + b,
    },
  ];

  return {
    label: 'Тренд',
    data: trendData,
    backgroundColor: backgroundColors[1],
    borderColor: backgroundColors[1],
  };
}

function getTimeFromUTC(date) {
  const dateObj = new Date(`${date}Z`);
  return dateObj.getTime();
}

function timeToUTC(date) {
  const dateObj = new Date(date);
  return dateObj.toISOString().slice(0, -1);
}

// Метод наименьших квадратов, для нахождения тренда, для типа {timeUTC: number, value: number}
// x - это "timeUTC"
// y - это "value"
// n - это количество элементов в массиве
// a - это коэффициент наклона
// b - это коэффициент смещения
// y = a * x + b
function getTrendCoefficients(arr) {
  const n = arr.length;

  let xSum = 0;
  let ySum = 0;
  let xySum = 0;
  let x2Sum = 0;

  arr.forEach((item) => {
    xSum += item.timeUTC;
    ySum += item.value;
    xySum += item.timeUTC * item.value;
    x2Sum += item.timeUTC ** 2;
  });

  const xSum2 = xSum ** 2;
  const xSumYSum = xSum * ySum;

  const a = (n * xySum - xSumYSum) / (n * x2Sum - xSum2);
  const b = (ySum - a * xSum) / n;

  return { a, b };
}
